import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Plus, Star, Eye, EyeOff, Edit2, Trash2, 
  MessageSquare, Search, Save, X, AlertCircle, ChevronUp, ChevronDown, Calendar, ExternalLink, Image
} from 'lucide-react';
import AdminSidebar from '../components/layout/AdminSidebar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminTestimonialsPage = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState('added_date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedTestimonial, setSelectedTestimonial] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    text: '',
    rating: 5,
    project_type: '',
    is_visible: true,
    added_date: new Date().toISOString().split('T')[0],
    linked_project_id: '',
    linked_project_slug: '',
    linked_project_title: ''
  });

  const fetchTestimonials = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/testimonials`);
      setTestimonials(response.data);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/testimonials/available-projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  useEffect(() => {
    fetchTestimonials();
    fetchProjects();
  }, []);

  // Sort and filter testimonials
  const sortedTestimonials = [...testimonials]
    .filter(t => {
      const query = searchQuery.toLowerCase();
      return t.name.toLowerCase().includes(query) ||
             t.text.toLowerCase().includes(query) ||
             t.location.toLowerCase().includes(query);
    })
    .sort((a, b) => {
      let aVal = a[sortField] || '';
      let bVal = b[sortField] || '';
      
      if (sortField === 'added_date') {
        aVal = new Date(aVal || '1970-01-01').getTime();
        bVal = new Date(bVal || '1970-01-01').getTime();
      } else if (sortField === 'rating') {
        aVal = parseInt(aVal) || 0;
        bVal = parseInt(bVal) || 0;
      }
      
      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const handleAddNew = () => {
    setSelectedTestimonial(null);
    setFormData({
      name: '',
      location: '',
      text: '',
      rating: 5,
      project_type: '',
      is_visible: true,
      added_date: new Date().toISOString().split('T')[0],
      linked_project_id: '',
      linked_project_slug: '',
      linked_project_title: ''
    });
    setIsModalOpen(true);
  };

  const handleEdit = (testimonial) => {
    setSelectedTestimonial(testimonial);
    setFormData({
      name: testimonial.name,
      location: testimonial.location,
      text: testimonial.text,
      rating: testimonial.rating,
      project_type: testimonial.project_type || '',
      is_visible: testimonial.is_visible,
      added_date: testimonial.added_date || '',
      linked_project_id: testimonial.linked_project_id || '',
      linked_project_slug: testimonial.linked_project_slug || '',
      linked_project_title: testimonial.linked_project_title || ''
    });
    setIsModalOpen(true);
  };

  const handleProjectSelect = (projectId) => {
    if (projectId === 'none') {
      setFormData({
        ...formData,
        linked_project_id: '',
        linked_project_slug: '',
        linked_project_title: ''
      });
    } else {
      const project = projects.find(p => p.id === projectId);
      if (project) {
        setFormData({
          ...formData,
          linked_project_id: project.id,
          linked_project_slug: project.slug,
          linked_project_title: project.title
        });
      }
    }
  };

  const handleSave = async () => {
    if (!formData.name || !formData.location || !formData.text) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      setIsSaving(true);
      
      const payload = {
        ...formData,
        linked_project_id: formData.linked_project_id || null,
        linked_project_slug: formData.linked_project_slug || null,
        linked_project_title: formData.linked_project_title || null
      };
      
      if (selectedTestimonial) {
        await axios.put(`${BACKEND_URL}/api/testimonials/${selectedTestimonial.id}`, payload);
      } else {
        await axios.post(`${BACKEND_URL}/api/testimonials`, {
          ...payload,
          source: 'manual'
        });
      }
      
      setIsModalOpen(false);
      fetchTestimonials();
    } catch (error) {
      console.error('Error saving testimonial:', error);
      alert('Error saving testimonial');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleVisibility = async (testimonial) => {
    try {
      await axios.put(`${BACKEND_URL}/api/testimonials/${testimonial.id}/visibility`);
      fetchTestimonials();
    } catch (error) {
      console.error('Error toggling visibility:', error);
    }
  };

  const handleDelete = async () => {
    if (!selectedTestimonial) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/testimonials/${selectedTestimonial.id}`);
      setIsDeleteDialogOpen(false);
      setSelectedTestimonial(null);
      fetchTestimonials();
    } catch (error) {
      console.error('Error deleting testimonial:', error);
    }
  };

  const confirmDelete = (testimonial) => {
    setSelectedTestimonial(testimonial);
    setIsDeleteDialogOpen(true);
  };

  const renderStars = (rating, editable = false, onChange = null) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            disabled={!editable}
            onClick={() => editable && onChange && onChange(star)}
            className={`${editable ? 'cursor-pointer hover:scale-110' : 'cursor-default'} transition-transform`}
          >
            <Star 
              className={`w-4 h-4 ${
                star <= rating 
                  ? 'fill-yellow-500 text-yellow-500' 
                  : 'text-slate-300'
              }`} 
            />
          </button>
        ))}
      </div>
    );
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ChevronUp className="w-3 h-3 text-slate-300" />;
    return sortDirection === 'asc' 
      ? <ChevronUp className="w-3 h-3 text-cyan-600" />
      : <ChevronDown className="w-3 h-3 text-cyan-600" />;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const visibleCount = testimonials.filter(t => t.is_visible).length;
  const avgRating = testimonials.length > 0 
    ? (testimonials.reduce((acc, t) => acc + t.rating, 0) / testimonials.length).toFixed(1)
    : '0';

  return (
    <AdminSidebar pageTitle="Testimonials" pageIcon={Star}>
      {/* Stats Bar */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4 text-sm">
          <span className="text-slate-500">Total: <strong className="text-slate-700">{testimonials.length}</strong></span>
          <span className="text-slate-500">Visible: <strong className="text-green-600">{visibleCount}</strong></span>
          <span className="text-slate-500 flex items-center gap-1">
            Avg: <strong className="text-yellow-600">{avgRating}</strong>
            <Star className="w-3 h-3 fill-yellow-500 text-yellow-500" />
          </span>
        </div>
        <Button onClick={handleAddNew} size="sm" className="bg-cyan-600 hover:bg-cyan-700 gap-1">
          <Plus className="w-4 h-4" />
          Add
        </Button>
      </div>

      {/* Search & Info Bar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search testimonials..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 h-9"
          />
        </div>
        <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-1.5">
          <AlertCircle className="w-4 h-4 text-blue-500" />
          <span className="text-xs text-blue-700">Google Reviews integration coming soon</span>
        </div>
      </div>

      {/* Table */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          {isLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin w-6 h-6 border-2 border-cyan-600 border-t-transparent rounded-full mx-auto"></div>
            </div>
          ) : sortedTestimonials.length === 0 ? (
            <div className="text-center py-12">
              <MessageSquare className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No testimonials found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left py-2 px-3 font-medium text-slate-600 w-8">
                      <button onClick={() => handleSort('is_visible')} className="flex items-center gap-1">
                        Vis
                      </button>
                    </th>
                    <th className="text-left py-2 px-3 font-medium text-slate-600">
                      <button onClick={() => handleSort('name')} className="flex items-center gap-1">
                        Name/Location <SortIcon field="name" />
                      </button>
                    </th>
                    <th className="text-left py-2 px-3 font-medium text-slate-600 w-16">
                      <button onClick={() => handleSort('rating')} className="flex items-center gap-1">
                        Rating <SortIcon field="rating" />
                      </button>
                    </th>
                    <th className="text-left py-2 px-3 font-medium text-slate-600 hidden lg:table-cell">
                      Testimonial
                    </th>
                    <th className="text-left py-2 px-3 font-medium text-slate-600 w-20">
                      Project
                    </th>
                    <th className="text-left py-2 px-3 font-medium text-slate-600 w-24">
                      <button onClick={() => handleSort('added_date')} className="flex items-center gap-1">
                        Added <SortIcon field="added_date" />
                      </button>
                    </th>
                    <th className="text-right py-2 px-3 font-medium text-slate-600 w-24">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {sortedTestimonials.map((testimonial) => (
                    <tr 
                      key={testimonial.id} 
                      className={`hover:bg-slate-50 ${!testimonial.is_visible ? 'opacity-50' : ''}`}
                    >
                      <td className="py-2 px-3">
                        <button
                          onClick={() => handleToggleVisibility(testimonial)}
                          className="p-1 hover:bg-slate-100 rounded"
                          title={testimonial.is_visible ? 'Hide' : 'Show'}
                        >
                          {testimonial.is_visible 
                            ? <Eye className="w-4 h-4 text-green-600" />
                            : <EyeOff className="w-4 h-4 text-slate-400" />
                          }
                        </button>
                      </td>
                      <td className="py-2 px-3">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 bg-cyan-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-cyan-600 font-bold text-xs">
                              {testimonial.name.charAt(0)}
                            </span>
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-slate-900 truncate">{testimonial.name}</p>
                            <p className="text-xs text-slate-500 truncate">{testimonial.location}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-2 px-3">
                        {renderStars(testimonial.rating)}
                      </td>
                      <td className="py-2 px-3 hidden lg:table-cell">
                        <p className="text-slate-600 line-clamp-2 text-xs leading-relaxed">
                          "{testimonial.text}"
                        </p>
                        {testimonial.project_type && (
                          <span className="inline-block mt-1 text-xs text-cyan-600 bg-cyan-50 px-1.5 py-0.5 rounded">
                            {testimonial.project_type}
                          </span>
                        )}
                      </td>
                      <td className="py-2 px-3">
                        {testimonial.linked_project_slug ? (
                          <Link 
                            to={`/portfolio/${testimonial.linked_project_slug}`}
                            target="_blank"
                            className="flex items-center gap-1 text-xs text-cyan-600 hover:text-cyan-700"
                            title={testimonial.linked_project_title}
                          >
                            <Image className="w-3.5 h-3.5" />
                            <span className="truncate max-w-[60px]">Linked</span>
                          </Link>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </td>
                      <td className="py-2 px-3 text-xs text-slate-500">
                        {formatDate(testimonial.added_date)}
                      </td>
                      <td className="py-2 px-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => handleEdit(testimonial)}
                            className="p-1.5 hover:bg-slate-100 rounded text-slate-600"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => confirmDelete(testimonial)}
                            className="p-1.5 hover:bg-red-50 rounded text-red-500"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

      {/* Add/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {selectedTestimonial ? 'Edit Testimonial' : 'Add New Testimonial'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="John D."
                  className="h-9"
                />
              </div>
              <div>
                <Label className="text-xs">Location *</Label>
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="Charlotte, NC"
                  className="h-9"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Rating</Label>
                <div className="mt-1.5">
                  {renderStars(formData.rating, true, (rating) => setFormData({ ...formData, rating }))}
                </div>
              </div>
              <div>
                <Label className="text-xs flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> Added Date
                </Label>
                <Input
                  type="date"
                  value={formData.added_date}
                  onChange={(e) => setFormData({ ...formData, added_date: e.target.value })}
                  className="h-9"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-xs">Testimonial Text *</Label>
              <Textarea
                value={formData.text}
                onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                placeholder="Write the testimonial..."
                rows={4}
                className="text-sm"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Project Type (Optional)</Label>
                <Input
                  value={formData.project_type}
                  onChange={(e) => setFormData({ ...formData, project_type: e.target.value })}
                  placeholder="e.g., Kitchen Remodel"
                  className="h-9"
                />
              </div>
              <div>
                <Label className="text-xs flex items-center gap-1">
                  <ExternalLink className="w-3 h-3" /> Linked Portfolio Project
                </Label>
                <Select 
                  value={formData.linked_project_id || 'none'} 
                  onValueChange={handleProjectSelect}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Select a project..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No linked project</SelectItem>
                    {projects.map((project) => (
                      <SelectItem key={project.id} value={project.id}>
                        {project.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_visible"
                checked={formData.is_visible}
                onChange={(e) => setFormData({ ...formData, is_visible: e.target.checked })}
                className="rounded border-slate-300"
              />
              <Label htmlFor="is_visible" className="text-sm text-slate-700 cursor-pointer">
                Show on website
              </Label>
            </div>
            
            <div className="flex justify-end gap-2 pt-3 border-t">
              <Button variant="outline" size="sm" onClick={() => setIsModalOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSave} 
                disabled={isSaving}
                size="sm"
                className="bg-cyan-600 hover:bg-cyan-700 gap-1"
              >
                {isSaving ? 'Saving...' : (
                  <>
                    <Save className="w-4 h-4" />
                    {selectedTestimonial ? 'Update' : 'Add'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Testimonial?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the testimonial from {selectedTestimonial?.name}. 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminSidebar>
  );
};

export default AdminTestimonialsPage;
